/**
 * Customer profile tab (SPEC screen 2).
 *
 * A party with no customer profile renders the enable affordance; enabling is
 * `PUT /parties/{id}/customer-profile`, whose If-Match on FIRST enable is the
 * strong party aggregate ETag. Subsequent edits use the merge-PATCH with the
 * profile-scoped validator captured from `GET …/customer-profile`.
 *
 * `credit_limit` is a NUMERIC(20,4) decimal STRING (`PartnersAmountString`) —
 * typed text is canonicalised to exactly four fractional digits by string
 * padding, never through `Number()`. Empty means null: no configured ceiling.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { formatQuantity } from "@/lib/format";

import {
  archiveCustomerProfile,
  getCustomerProfile,
  putCustomerProfile,
  updateCustomerProfile,
  type CustomerProfile,
  type CustomerProfilePatch,
  type CustomerProfilePut,
} from "../api";
import { AMOUNT_FRACTION_DIGITS, toAmountString } from "../decimal";
import { partnersKeys, useCustomerGroups, usePaymentTerms } from "../queries";

export interface CustomerProfilePanelProps {
  partyId: string;
  partyEtag: string | null;
  profile?: CustomerProfile;
  currency: string;
  /** Archived / pseudonymised parties are read-only regardless of grants. */
  partyEditable: boolean;
  canRead?: boolean;
  canUpdate?: boolean;
  canArchive?: boolean;
  canReadGroups?: boolean;
  canReadPaymentTerms?: boolean;
}

interface Draft {
  groupId: string;
  paymentTermsId: string;
  creditLimit: string;
  creditHold: boolean;
}

function draftFrom(profile: CustomerProfile | undefined): Draft {
  return {
    groupId: profile?.customer_group_id ?? "",
    paymentTermsId: profile?.payment_terms_id ?? "",
    creditLimit: profile?.credit_limit ?? "",
    creditHold: profile?.credit_hold ?? false,
  };
}

export function CustomerProfilePanel({
  partyId,
  partyEtag,
  profile,
  currency,
  partyEditable,
  canRead = true,
  canUpdate = true,
  canArchive = true,
  canReadGroups = true,
  canReadPaymentTerms = true,
}: CustomerProfilePanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const exists = profile !== undefined && profile.status === "active";
  const editable = canUpdate && partyEditable;

  const [draft, setDraft] = useState<Draft>(() => draftFrom(profile));
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [holdConfirm, setHoldConfirm] = useState<boolean | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState(false);

  useEffect(() => {
    setDraft(draftFrom(profile));
    setErrors({});
  }, [profile]);

  const groupsQuery = useCustomerGroups(canReadGroups);
  const termsQuery = usePaymentTerms(canReadPaymentTerms);
  const groups = (groupsQuery.data ?? []).filter(
    (group) => group.status === "active" || group.id === draft.groupId,
  );
  const terms = (termsQuery.data ?? []).filter(
    (term) => term.status === "active" || term.id === draft.paymentTermsId,
  );

  /**
   * The profile-scoped strong validator. The contract also accepts the party
   * aggregate ETag, which is the only one that exists before the first enable.
   */
  const profileEtagQuery = useQuery({
    queryKey: [...partnersKeys.parties.detail(partyId), "customer-profile"],
    queryFn: () => getCustomerProfile(partyId),
    enabled: canRead && exists,
  });
  const ifMatch = profileEtagQuery.data?.etag ?? partyEtag;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
  };

  const enableMutation = useMutation({
    mutationFn: (input: CustomerProfilePut) => putCustomerProfile(partyId, input, ifMatch ?? ""),
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const patchMutation = useMutation({
    mutationFn: (patch: CustomerProfilePatch) =>
      updateCustomerProfile(partyId, patch, ifMatch ?? ""),
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveCustomerProfile(partyId),
    onSuccess: () => {
      setArchiveConfirm(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const validate = (): { creditLimit: string | null } | null => {
    const next: Partial<Record<keyof Draft, string>> = {};
    if (draft.groupId === "") next.groupId = t("partners.customer.groupRequired");
    if (draft.paymentTermsId === "") next.paymentTermsId = t("partners.customer.termsRequired");
    let creditLimit: string | null = null;
    if (draft.creditLimit.trim() !== "") {
      creditLimit = toAmountString(draft.creditLimit);
      if (creditLimit === null) next.creditLimit = t("partners.customer.amountInvalid");
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return null;
    }
    setErrors({});
    return { creditLimit };
  };

  const save = (): void => {
    const validated = validate();
    if (validated === null) return;
    if (!exists) {
      enableMutation.mutate({
        customer_group_id: draft.groupId,
        payment_terms_id: draft.paymentTermsId,
        credit_limit: validated.creditLimit,
        credit_hold: draft.creditHold,
      });
      return;
    }
    patchMutation.mutate({
      customer_group_id: draft.groupId,
      payment_terms_id: draft.paymentTermsId,
      credit_limit: validated.creditLimit,
    });
  };

  const pending = enableMutation.isPending || patchMutation.isPending;

  const renderCreditLimit = (amount: string): string => {
    try {
      return formatQuantity(amount, i18n.language, AMOUNT_FRACTION_DIGITS);
    } catch {
      return amount;
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle>
          <h2>{t("partners.customer.title")}</h2>
        </CardTitle>
        {exists && canArchive && partyEditable ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setArchiveConfirm(true)}>
            {t("partners.customer.disable")}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!exists ? (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{t("partners.customer.notEnabled")}</p>
            <p className="text-sm text-muted-foreground">{t("partners.customer.enableHint")}</p>
          </div>
        ) : null}

        {!editable ? (
          <p className="text-sm text-muted-foreground">{t("partners.customer.readOnly")}</p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-group">{t("partners.customer.group")}</Label>
            <Select
              value={draft.groupId}
              disabled={!editable || !canReadGroups}
              onValueChange={(value) => {
                setDraft((current) => ({ ...current, groupId: value }));
                setErrors((current) => ({ ...current, groupId: undefined }));
              }}
            >
              <SelectTrigger id="customer-group" aria-invalid={errors.groupId !== undefined}>
                <SelectValue placeholder={t("partners.common.none")} />
              </SelectTrigger>
              <SelectContent>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.groupId !== undefined ? (
              <p role="alert" className="text-sm text-destructive">
                {errors.groupId}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-terms">{t("partners.customer.paymentTerms")}</Label>
            <Select
              value={draft.paymentTermsId}
              disabled={!editable || !canReadPaymentTerms}
              onValueChange={(value) => {
                setDraft((current) => ({ ...current, paymentTermsId: value }));
                setErrors((current) => ({ ...current, paymentTermsId: undefined }));
              }}
            >
              <SelectTrigger id="customer-terms" aria-invalid={errors.paymentTermsId !== undefined}>
                <SelectValue placeholder={t("partners.common.none")} />
              </SelectTrigger>
              <SelectContent>
                {terms.map((term) => (
                  <SelectItem key={term.id} value={term.id}>
                    {term.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.paymentTermsId !== undefined ? (
              <p role="alert" className="text-sm text-destructive">
                {errors.paymentTermsId}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-credit-limit">{t("partners.customer.creditLimit")}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="customer-credit-limit"
                // physical-ok: decimal amounts are entered and read LTR in every locale
                dir="ltr"
                inputMode="decimal"
                className="text-end tabular-nums"
                value={draft.creditLimit}
                disabled={!editable}
                aria-invalid={errors.creditLimit !== undefined}
                onChange={(event) => {
                  setDraft((current) => ({ ...current, creditLimit: event.target.value }));
                  setErrors((current) => ({ ...current, creditLimit: undefined }));
                }}
              />
              <span dir="ltr" className="text-sm text-muted-foreground">
                {currency}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {draft.creditLimit.trim() === ""
                ? t("partners.customer.unlimited")
                : t("partners.customer.creditLimitHint")}
            </p>
            {errors.creditLimit !== undefined ? (
              <p role="alert" className="text-sm text-destructive">
                {errors.creditLimit}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t("partners.customer.creditHold")}</span>
            <div className="flex items-center gap-2">
              <Checkbox
                id="customer-credit-hold"
                checked={draft.creditHold}
                disabled={!editable}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  if (!exists) {
                    setDraft((current) => ({ ...current, creditHold: next }));
                    return;
                  }
                  setHoldConfirm(next);
                }}
              />
              <Label htmlFor="customer-credit-hold" className="font-normal">
                {t("partners.customer.creditHold")}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">{t("partners.customer.creditHoldHint")}</p>
          </div>
        </div>

        {exists && profile?.credit_limit != null ? (
          <p className="text-xs text-muted-foreground" data-testid="customer-credit-limit-current">
            {t("partners.customer.creditLimit")}:{" "}
            <span dir="ltr" className="tabular-nums">
              {renderCreditLimit(profile.credit_limit)} {currency}
            </span>
          </p>
        ) : null}

        {editable ? (
          <div>
            <Button type="button" loading={pending} onClick={save}>
              {exists ? t("partners.customer.save") : t("partners.customer.enable")}
            </Button>
          </div>
        ) : null}
      </CardContent>

      <Dialog open={holdConfirm !== null} onOpenChange={() => setHoldConfirm(null)}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>
              {holdConfirm === true
                ? t("partners.customer.creditHoldOnTitle")
                : t("partners.customer.creditHoldOffTitle")}
            </DialogTitle>
            <DialogDescription>
              {holdConfirm === true
                ? t("partners.customer.creditHoldOnBody")
                : t("partners.customer.creditHoldOffBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setHoldConfirm(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={patchMutation.isPending}
              onClick={() => {
                const next = holdConfirm === true;
                setHoldConfirm(null);
                patchMutation.mutate({ credit_hold: next });
              }}
            >
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveConfirm} onOpenChange={setArchiveConfirm}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.customer.disableTitle")}</DialogTitle>
            <DialogDescription>{t("partners.customer.disableBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveConfirm(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate()}
            >
              {t("partners.customer.disable")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
